import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Table, Thead, Tbody, Tr, Th } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';

import { Months } from '../../utils/contants/months';
import { getDate } from '../../helpers/dates';

import './styles.css';
import AuthContext from '../../contexts/AuthContext';

export type CashflowInlineFilterState = {
  client?: string;
  mes?: string;
  ano?: string;
  data?: string;
  conta?: string;
  condicao?: string;
  origem?: string;
  historico?: string;
  valor?: string;
  status?: string;
};

interface IProps {
  filters: CashflowInlineFilterState;
  onFilterChange(filters: CashflowInlineFilterState): void;
  children?: React.ReactNode;
}

const defaultFilters: CashflowInlineFilterState = {
  client: '',
  mes: '',
  ano: '',
  data: '',
  conta: '',
  condicao: '',
  origem: '',
  historico: '',
  valor: '',
  status: '',
};

const statusSelectOptions = [
  { label: 'Todos', value: '' },
  { label: 'Criado', value: 'criado' },
  { label: 'Em análise', value: 'em análise' },
  { label: 'Finalizado', value: 'finalizado' },
];

const condicaoSelectOptions = [
  { label: 'Todas', value: '' },
  { label: 'A pagar', value: 'A pagar' },
  { label: 'Pago', value: 'Pago' },
  { label: 'A receber', value: 'A receber' },
  { label: 'Recebido', value: 'Recebido' },
];

const origemSelectOptions = [
  { label: 'Todas', value: '' },
  { label: 'Entrada', value: 'Entrada' },
  { label: 'Saída', value: 'Saída' },
];

const CashflowTableInlineFilters: React.FC<IProps> = ({ filters, onFilterChange, children }) => {
  const [localFilters, setLocalFilters] = useState<CashflowInlineFilterState>({ ...defaultFilters, ...filters });

  const { userType } = useContext(AuthContext);
  const showClientFilter = userType !== "Consumer";

  useEffect(() => {
    setLocalFilters({ ...defaultFilters, ...filters });
  }, [filters]);

  useEffect(() => {
    const handler = setTimeout(() => {
      onFilterChange(localFilters);
    }, 300);

    return () => clearTimeout(handler);
  }, [localFilters, onFilterChange]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear + 5;
    const endYear = currentYear - 9;

    const years: string[] = [];
    for (let year = startYear; year >= endYear; year -= 1) {
      years.push(String(year));
    }

    return years;
  }, []);

  const sanitizeDecimalInput = (value: string): string => {
    if (!value) return '';

    const digitsOnly = value.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
    const parts = digitsOnly.split('.');
    const integerPart = parts.shift() || '';
    const decimalPart = parts.join('');
    const endsWithDot = digitsOnly.endsWith('.');

    let normalizedInteger = integerPart.replace(/^0+(?=\d)/, '');
    if (digitsOnly.startsWith('.') && normalizedInteger === '') {
      normalizedInteger = '0';
    }

    let normalizedValue = normalizedInteger;

    if (decimalPart) {
      normalizedValue = `${normalizedValue === '' ? '0' : normalizedValue}.${decimalPart}`;
    } else if (endsWithDot) {
      normalizedValue = `${normalizedValue === '' ? '0' : normalizedValue}.`;
    }

    return normalizedValue;
  };

  const handleChange = (key: keyof CashflowInlineFilterState, value: string) => {
    let nextValue = value;

    if (key === 'valor') {
      nextValue = sanitizeDecimalInput(value);
    }

    setLocalFilters((prev) => ({
      ...prev,
      [key]: nextValue,
    }));
  };

  return (
    <Table className="inline-filters-table">
      <Thead>
        <Tr>
          {showClientFilter ? (
            <Th>
              <span className="column-label">Cliente</span>
              <input
                className="filter-input"
                placeholder="Pesquisar cliente"
                value={localFilters.client || ''}
                onChange={(event) => handleChange('client', event.target.value)}
              />
            </Th>
          ) : (
            <Th>
              {/* <span className="column-label">Cliente</span> */}
            </Th>
          )}
          <Th>
            <span className="column-label">Mês</span>
            <select
              className="filter-select"
              value={localFilters.mes || ''}
              onChange={(event) => handleChange('mes', event.target.value)}
            >
              <option value="">Todos</option>
              {Months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </Th>
          <Th>
            <span className="column-label">Ano</span>
            <select
              className="filter-select"
              value={localFilters.ano || ''}
              onChange={(event) => handleChange('ano', event.target.value)}
            >
              <option value="">Todos</option>
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </Th>
          <Th>
            <span className="column-label">Data</span>
            <input
              type="date"
              className="filter-input"
              value={localFilters.data || ''}
              max={getDate.endOfCurrentMonth()}
              onChange={(event) => handleChange('data', event.target.value)}
            />
          </Th>
          <Th>
            <span className="column-label">Conta</span>
            <input
              className="filter-input"
              placeholder="Conta"
              value={localFilters.conta || ''}
              onChange={(event) => handleChange('conta', event.target.value)}
            />
          </Th>
          <Th>
            <span className="column-label">Condição</span>
            <select
              className="filter-select"
              value={localFilters.condicao || ''}
              onChange={(event) => handleChange('condicao', event.target.value)}
            >
              {condicaoSelectOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Th>
          <Th>
            <span className="column-label">Origem</span>
            <select
              className="filter-select"
              value={localFilters.origem || ''}
              onChange={(event) => handleChange('origem', event.target.value)}
            >
              {origemSelectOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Th>
          <Th>
            <span className="column-label">Histórico</span>
            <input
              className="filter-input"
              placeholder="Histórico"
              value={localFilters.historico || ''}
              onChange={(event) => handleChange('historico', event.target.value)}
            />
          </Th>
          <Th>
            <span className="column-label">Valor</span>
            <input
              type="text"
              className="filter-input"
              placeholder="0,00"
              value={localFilters.valor !== undefined && localFilters.valor !== null ? String(localFilters.valor) : ''}
              onChange={(event) => handleChange('valor', event.target.value)}
            />
          </Th>
          <Th>
            <span className="column-label">Status</span>
            <select
              className="filter-select"
              value={localFilters.status || ''}
              onChange={(event) => handleChange('status', event.target.value)}
            >
              {statusSelectOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </Th>
          <Th className="actions-header">
            <span className="column-label">#</span>
          </Th>
        </Tr>
      </Thead>
      <Tbody>{children}</Tbody>
    </Table>
  );
};

export default CashflowTableInlineFilters;
